import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBiasDetail } from '../backend/fetchs/bias';
import { DailyBias } from '../backend/interfaces/bias';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import IntervalCell from '../components/IntervalCell';

const BiasDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DailyBias | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const result = await fetchBiasDetail(parseInt(id));
        setData(result);
        setError(null);
      } catch (err) {
        setError('Error fetching bias detail');
        console.error('Error fetching bias detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error || 'Bias not found'}</div>
        <Link
          to="/bias"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Bias
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 8,
    }).format(price);
  };

  const formatVolume = (volume: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(volume);
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Daily Bias #{data.id}</h1>
        </div>
        
        {/* Bias Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-medium text-gray-500">Ticker</h2>
              <p className="text-2xl font-bold text-gray-900">{data.ticker.description}</p>
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Confirmation Date</h2>
              <DateCell timestamp={data.created_at} blackMode={true} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Daily Bias</h2>
              <DirectionCell direction={data.side.description} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Interval</h2>
              <IntervalCell interval={data.candlestick.interval.description} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Bias Type</h2>
              <DirectionCell direction={data.type.description} />
            </div>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Explanation</h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-md text-green-700">
              A daily bias of <strong>{data.side.description}</strong> was detected for {data.ticker.description}.
            </span>
          </div>
        </div>
      </div>

      {/* Candlestick Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Candlestick Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open Date</h3>
              <DateCell timestamp={data.candlestick.open_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close Date</h3>
              <DateCell timestamp={data.candlestick.close_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Volume</h3>
              <p className="text-lg font-semibold text-gray-900">{formatVolume(data.candlestick.volume)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Number of Trades</h3>
              <p className="text-lg font-semibold text-gray-900">
                {data.candlestick.number_of_trades.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.open)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.close)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">High</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.high)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Low</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.low)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiasDetailPage; 